/* Runs before retail scripts so checkout returns cannot trigger retail gift additions. */
(() => {
  const key = 'rasta-wholesale:session:v1';
  const config = window.RastaWholesaleSessionConfig || {};
  let leaving = null;
  const read = () => {
    try { const value = localStorage.getItem(key); if (value) return JSON.parse(value); } catch (_) {}
    try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (_) { return null; }
  };
  const write = (state) => {
    let saved = false;
    for (const name of ['localStorage', 'sessionStorage']) {
      try { window[name].setItem(key, JSON.stringify(state)); saved = true; } catch (_) {}
    }
    if (!saved) throw new Error('Allow browser storage to keep wholesale and normal shopping separate.');
  };
  const portalUrl = (value) => {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.includes('/pages/')) throw new Error('The wholesale return page is invalid.');
    const preview = url.searchParams.get('preview_theme_id');
    url.search = ''; url.hash = '';
    url.searchParams.set('view', 'wholesale');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.href;
  };
  const isWholesaleLine = (item) => !!item.properties?._rasta_wholesale_request;
  const isGift = (item) => String(item.properties?._cs_free_gift || '').toLowerCase() === 'true';
  const request = async (path, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch((config.root || '/') + path, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      const cart = await response.json();
      if (!response.ok || !Array.isArray(cart?.items)) throw new Error('We could not clear your wholesale cart. Please try again.');
      return cart;
    } finally { clearTimeout(timer); }
  };
  const showError = (error) => {
    document.documentElement.classList.remove('rw-session-transition');
    const show = () => {
      let notice = document.getElementById('rw-session-error');
      if (!notice) {
        notice = document.createElement('div'); notice.id = 'rw-session-error'; notice.setAttribute('role', 'alert');
        notice.style.cssText = 'position:fixed;inset:0;z-index:2147483647;display:grid;place-content:center;gap:20px;padding:24px;background:#faf9f3;color:#173f2b;font:16px/1.6 sans-serif';
        document.body.append(notice);
      }
      notice.replaceChildren();
      const message = document.createElement('p'); message.textContent = error.message;
      const retry = document.createElement('button'); retry.textContent = 'Try again'; retry.onclick = () => window.location.reload();
      const back = document.createElement('a'); back.textContent = 'Back to wholesale'; back.href = read()?.portalUrl || config.portalUrl || '/';
      notice.append(message, retry, back);
    };
    if (document.body) show(); else document.addEventListener('DOMContentLoaded', show, { once: true });
  };
  const session = window.RastaWholesaleSession = {
    ready: Promise.resolve(),
    read,
    active() { return !!config.isWholesale || !!config.hasWholesaleCart || (!!read() && read().stage !== 'retail'); },
    ownsLine: isWholesaleLine,
    enter(value) {
      const previous = read();
      const returned = previous?.stage === 'checkout' ? previous.quantities : null;
      const checkoutVisited = previous?.stage !== 'retail' && (previous?.stage === 'checkout' || previous?.checkoutVisited === true);
      write({ stage: 'shopping', portalUrl: portalUrl(value), quantities: {}, tokens: [], checkoutVisited });
      return returned;
    },
    rememberCart(cart) {
      const state = read();
      if (!state?.portalUrl) throw new Error('Reopen the wholesale portal before checkout.');
      const quantities = {};
      for (const item of cart.items) quantities[item.variant_id] = (quantities[item.variant_id] || 0) + item.quantity;
      write({ ...state, quantities, tokens: [...new Set(cart.items.map(item => item.properties?._rasta_wholesale_request).filter(Boolean))] });
    },
    markCheckout(cart) { this.rememberCart(cart); write({ ...read(), stage: 'checkout', checkoutVisited: true }); },
    returnUrl() { return portalUrl(read()?.portalUrl || config.portalUrl); },
    ownsStoredLine(item) { return (read()?.tokens || []).includes(item.properties?._rasta_wholesale_request); },
    async leave(destination) {
      if (leaving) return leaving;
      const previous = read();
      document.documentElement.classList.add('rw-session-transition');
      this.ready = leaving = (async () => {
        if (previous) write({ ...previous, stage: 'leaving' });
        let cart = await request('cart.js');
        // Gift lines appended while this was a wholesale cart must not survive switching to retail.
        const hadWholesale = !!previous && previous.stage !== 'retail' || config.hasWholesaleCart || cart.items.some(isWholesaleLine);
        const remove = item => isWholesaleLine(item) || (hadWholesale && isGift(item));
        for (let attempt = 0; attempt < 2; attempt++) {
          const outside = cart.items.filter(remove);
          if (!outside.length) break;
          if (outside.some(item => !item.key)) throw new Error('We could not identify a wholesale cart item. Please reload.');
          await request('cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: Object.fromEntries(outside.map(item => [item.key, 0])) }) });
          cart = await request('cart.js');
        }
        if (cart.items.some(remove)) throw new Error('Your wholesale cart is still updating. Please try switching to the normal store again.');
        // Keep a retail tombstone so stale sessionStorage in another tab cannot restore wholesale mode.
        write({ stage: 'retail', portalUrl: previous?.portalUrl || config.portalUrl, quantities: {}, tokens: [] });
        for (const name of ['localStorage', 'sessionStorage']) {
          try { window[name].removeItem('cs_cart_pending_grouped_sync_v1'); } catch (_) {}
        }
        if (destination) window.location.assign(destination);
        else window.location.replace(window.location.href);
      })().catch(error => { showError(error); throw error; }).finally(() => { leaving = null; });
      this.ready.catch(() => {});
      return this.ready;
    },
  };
  const handleArrival = () => {
    if (config.isWholesale || leaving) return;
    const state = read();
    // An old checkout can lose its return URL after the portal clears the cart.
    // Retain wholesale routing until an intentional retail link completes leave().
    if (state?.stage === 'checkout' || state?.stage === 'shopping' && state.checkoutVisited === true) {
      try {
        document.documentElement.classList.add('rw-session-transition');
        window.location.replace(portalUrl(state.portalUrl));
      } catch (error) { showError(error); }
    } else if (state && state.stage !== 'retail' || config.hasWholesaleCart) {
      session.leave().catch(() => {});
    }
  };
  // Intentional store navigation differs from a checkout's return-to-store navigation.
  document.addEventListener('click', (event) => {
    if (!config.isWholesale || !session.active() || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname === window.location.pathname && (url.search === window.location.search || url.searchParams.get('view') === 'wholesale')) return;
    if (url.searchParams.get('view') === 'wholesale' || url.pathname.includes('/account') || url.pathname.includes('/checkout')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    session.leave(url.href).catch(() => {});
  }, true);
  window.addEventListener('pageshow', handleArrival);
  window.addEventListener('focus', handleArrival);
  handleArrival();
})();
